'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import {
    ArrowLeft,
    TrendingUp,
    Save,
    Calendar,
    FileText,
    Building2,
    Briefcase,
    CheckCircle2
} from 'lucide-react';
import Link from 'next/link';

export default function AddSetoranModalPage() {
    const router = useRouter();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        description: '',
        type: 'Modal Tunai',
        amount: '',
        source: 'Yayasan Pusat',
        targetBank: 'Bank Mandiri - Operasional'
    });

    const capitalTypes = ['Modal Tunai', 'Hibah Terikat', 'Modal Kerja', 'Investasi Aset'];
    const sources = ['Yayasan Pusat', 'Donatur Tetap', 'Pemerintah', 'CSR Perusahaan', 'Lainnya'];
    const bankAccounts = ['Bank Mandiri - Operasional', 'BSI - Pembangunan', 'Bank Riau Kepri - Kas', 'Kas Tunai Unit'];

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate API call
        setTimeout(() => {
            setIsSubmitting(false);
            setIsSuccess(true);
            setTimeout(() => {
                router.push('/sekolah/setoran-modal');
            }, 1500);
        }, 1000);
    };

    if (isSuccess) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-in fade-in zoom-in duration-500">
                <div className="w-24 h-24 bg-success-soft rounded-full flex items-center justify-center mb-6 shadow-xl shadow-success/20">
                    <CheckCircle2 className="w-12 h-12 text-success" />
                </div>
                <h2 className="text-3xl font-black text-foreground mb-2">Berhasil Disimpan!</h2>
                <p className="text-muted font-medium mb-8">Data setoran modal telah berhasil dicatat dalam sistem.</p>
                <div className="animate-pulse flex gap-2 items-center text-sm font-bold text-primary">
                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                    Redirecting...
                </div>
            </div>
        );
    }

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-5xl mx-auto w-full pb-32 animate-in slide-in-from-right duration-500">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                        <Link href="/sekolah/setoran-modal" className="hover:text-primary transition-all">Setoran Modal</Link>
                        <span className="text-muted/50">•</span>
                        <span className="text-foreground">Input Baru</span>
                    </div>
                    <h1 className="text-3xl font-black text-foreground tracking-tight">Input Modal Baru</h1>
                </div>
                <Link
                    href="/sekolah/setoran-modal"
                    className="group flex items-center gap-2 text-secondary hover:text-primary transition-all bg-surface px-4 py-2 rounded-xl border border-border-main shadow-sm"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Kembali</span>
                </Link>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Form */}
                <div className="lg:col-span-2 space-y-8">
                    <div className="bg-surface border border-border-main rounded-3xl p-8 space-y-6 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Date */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-muted uppercase tracking-wider">Tanggal Transaksi</label>
                                <div className="relative">
                                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                                    <input
                                        type="date"
                                        required
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-xl text-sm font-semibold focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
                                    />
                                </div>
                            </div>

                            {/* Type */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-muted uppercase tracking-wider">Jenis Modal</label>
                                <div className="relative">
                                    <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                                    <select
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                        className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-xl text-sm font-semibold focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none appearance-none cursor-pointer"
                                    >
                                        {capitalTypes.map((type) => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-muted uppercase tracking-wider">Keterangan / Peruntukan</label>
                            <div className="relative">
                                <FileText className="absolute left-4 top-3.5 w-4 h-4 text-muted" />
                                <textarea
                                    required
                                    rows={3}
                                    placeholder="Contoh: Modal awal operasional semester genap 2024"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-xl text-sm font-semibold focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none resize-none"
                                />
                            </div>
                        </div>

                        {/* Amount */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-muted uppercase tracking-wider">Nominal (Rp)</label>
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-black text-muted">Rp</span>
                                <input
                                    type="number"
                                    required
                                    min="0"
                                    placeholder="0"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    className="w-full pl-10 pr-4 py-3 bg-background border border-border-main rounded-xl text-lg font-black tracking-tight focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar Details */}
                <div className="space-y-6">
                    <div className="bg-surface border border-border-main rounded-3xl p-6 space-y-6 shadow-sm">
                        <h3 className="text-sm font-black text-foreground border-b border-border-main pb-4">Sumber & Tujuan</h3>

                        {/* Source */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-muted uppercase tracking-wider">Sumber Dana</label>
                            <div className="relative">
                                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                                <select
                                    value={formData.source}
                                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                                    className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-xl text-sm font-semibold focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none appearance-none cursor-pointer"
                                >
                                    {sources.map((source) => (
                                        <option key={source} value={source}>{source}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Target Bank */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-muted uppercase tracking-wider">Rekening Tujuan</label>
                            <div className="relative">
                                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                                <select
                                    value={formData.targetBank}
                                    onChange={(e) => setFormData({ ...formData, targetBank: e.target.value })}
                                    className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-xl text-sm font-semibold focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none appearance-none cursor-pointer"
                                >
                                    {bankAccounts.map((acc) => (
                                        <option key={acc} value={acc}>{acc}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-primary text-white rounded-2xl text-sm font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:bg-primary/90 hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all"
                    >
                        {isSubmitting ? (
                            <span className="animate-pulse">Menyimpan...</span>
                        ) : (
                            <>
                                <Save className="w-5 h-5" /> Simpan Modal
                            </>
                        )}
                    </button>
                </div>
            </form>
        </main>
    );
}
